import { Link } from 'react-router-dom';
import { Instagram, Sparkles } from 'lucide-react';
import { useI18n } from '../i18n';
import { BrandWordmark } from './BrandLogo';

export default function Footer() {
  const { t } = useI18n();
  // 4-column premium footer (Sprint 5). Each column is a logical
  // grouping — Product (the catalog and pricing), Support (help and
  // contact), Company (about, blog, etc.) and a Follow-us column with
  // the Instagram social link plus the brand wordmark and copyright.
  // On phones the columns stack 2×2 (sm) → 1 column (xs) so the
  // footer still fits within the viewport.
  const COLS = [
    {
      title: t('footerProduct'),
      links: [
        { label: t('footerTemplates'), to: '/#templates', external: false },
        { label: t('footerPricing'),   to: '/pricing',    external: false },
        { label: t('footerExamples'),  to: '/examples',   external: false },
      ],
    },
    {
      title: t('footerSupport'),
      links: [
        { label: t('footerHelp'),    to: '/help',    external: false },
        { label: t('footerContact'), to: '/contact', external: false },
        { label: t('privacy'),       to: '/privacy', external: false },
        { label: t('terms'),         to: '/terms',   external: false },
      ],
    },
    {
      title: t('footerCompany'),
      links: [
        { label: t('footerAbout'),        to: '/about',         external: false },
        { label: t('footerBlog'),         to: '/blog',          external: false },
        { label: t('footerResources'),    to: '/resources',     external: false },
        // Step 2 (Site Infrastructure) — Learning Center + versioned
        // changelog. Keys are present in all 9 locale bundles.
        { label: t('footerLearn'),        to: '/learn',         external: false },
        { label: t('footerReleaseNotes'), to: '/release-notes', external: false },
      ],
    },
  ];

  return (
    <footer className="mt-auto border-t border-zinc-200 bg-zinc-50">
      {/* Link columns block — kept light to match the marketing page. Spacing
          tightened from py-10/12 → py-7/8 and gap from 8/10 → 6/8 so the
          footer doesn't dominate the home page anymore. */}
      <div className="max-w-[1600px] mx-auto px-3 sm:px-5 py-7 sm:py-8">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
          {/* 3 link columns */}
          {COLS.map((col) => (
            <div key={col.title}>
              <h4 className="font-chunky uppercase tracking-[0.18em] text-[11px] text-zinc-950 mb-3">
                {col.title}
              </h4>
              <ul className="space-y-2">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.external ? (
                      <a
                        href={link.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-zinc-600 text-sm hover:text-cyan-700 transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.to}
                        className="text-zinc-600 text-sm hover:text-cyan-700 transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {/* Follow-us column — Instagram + small "made with" tagline */}
          <div>
            <h4 className="font-chunky uppercase tracking-[0.18em] text-[11px] text-zinc-950 mb-3">
              {t('footerFollow')}
            </h4>
            <a
              href="https://www.instagram.com/makercutt"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('followInstagram')}
              title={t('followInstagram')}
              className="inline-flex items-center gap-2 text-zinc-700 text-sm hover:text-cyan-700 transition-colors"
            >
              <span className="grid place-items-center w-9 h-9 rounded-full bg-gradient-to-tr from-fuchsia-500 via-rose-500 to-amber-500 text-white shadow-sm">
                <Instagram className="w-4 h-4" />
              </span>
              <span className="font-chunky">@makercutt</span>
            </a>
            <p className="mt-3 text-zinc-500 text-xs leading-relaxed flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-cyan-600" />
              <span className="font-mono italic">{t('madeWith')}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Bottom bar — dark band so the BrandWordmark (which uses text-zinc-50
          + cyan accent) actually pops. This matches FooterMini, giving every
          page the same dark lockup at the very bottom. */}
      <div className="bg-zinc-950 border-t border-zinc-900">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-5 py-3 sm:py-4 flex items-center justify-between gap-3 flex-wrap">
          <Link to="/" className="hover:opacity-80 transition-opacity" aria-label="MakerCutt — home">
            <BrandWordmark />
          </Link>
          <span className="text-zinc-500 text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.18em]">
            {t('copyright')}
          </span>
        </div>
      </div>
    </footer>
  );
}
